import { fromJS, OrderedMap } from 'immutable';
import moment from 'moment-timezone';
import EventFormActions from 'flux/actions/EventFormActions';
import TeamActions from 'flux/actions/TeamActions';
import RealTimeActions from 'flux/actions/RealTimeActions';
import EventActions from 'flux/actions/EventActions';
import RestoreEventActions from 'flux/actions/RestoreEventActions';
import LabelListActions from 'flux/actions/LabelListActions';
import LabelEditFormActions from 'flux/actions/LabelEditFormActions';
import Pagination from 'shared/constants/Pagination';
import { UPCOMING, PAST, DELETED } from 'shared/constants/EventTypes';
import AlertActions from 'flux/actions/AlertActions';
import AlertTypes from 'shared/constants/AlertTypes';
import sortHelper from 'shared/lib/sort';

let onCreateFromProduct = EventFormActions.createFromProduct.completed,
  onCreate = EventFormActions.create.completed,
  onSave = EventFormActions.save.completed,
  onUpdateOwner = EventFormActions.updateOwnerPersistent.completed,
  onUpdateLabelsPersistentCompleted =
    EventFormActions.updateLabelsPersistent.completed,
  onUpdateEventLabels = EventFormActions.updateLabelsPersistent,
  onUpdateLabel = LabelEditFormActions.update,
  onUpdateTeamPhoto = TeamActions.updatePhoto.completed,
  onUpdateEventPhoto = EventActions.updatePhoto.completed,
  onLabelDeleted = LabelListActions.softDelete;

/**
 * EventListMixin
 *
 * Maintain event list state
 *
 */
export default {
  init() {
    let initialListState = {};

    [Pagination.PAGE_ITEMS_KEY].forEach((k) => (initialListState[k] = []));

    this.setState(fromJS(initialListState));

    this.listenTo(onLabelDeleted, 'onLabelDeleted');
    this.listenTo(onCreateFromProduct, 'onCreateFromProductCompleted');
    this.listenTo(onCreate, 'onCreateCompleted');
    this.listenTo(onSave, 'onSaveCompleted');
    this.listenTo(onUpdateOwner, 'onSaveCompleted');
    this.listenTo(onUpdateLabelsPersistentCompleted, 'onUpdateLabelsCompleted');
    this.listenTo(onUpdateEventLabels, 'onUpdateEventLabels');
    this.listenTo(onUpdateLabel, 'onUpdateLabel');
    this.listenTo(onUpdateTeamPhoto, 'onUpdateTeam');
    this.listenTo(onUpdateEventPhoto, 'onSaveCompleted');
  },

  onLabelDeleted(teamId, labelId) {
    this._removeAllLabelReferences(labelId);
  },

  onUpdateLabel(teamId, labelId, changes) {
    this._updateAllLabelReferences(labelId, changes);
  },

  onUpdateLabelsCompleted(event) {
    this.appendNew(event);
    this._quitEditing();
  },

  onUpdateEventLabels(teamId, eventId, labels) {
    this._updateLabelReferencesTransient(eventId, labels);
  },

  /**
   * Update teams with current props for event list
   *
   * @param team {Object}
   */
  onUpdateTeam(team) {
    if (!this.getInState(Pagination.PAGE_ITEMS_KEY)) return;

    let next = this.getState().update(Pagination.PAGE_ITEMS_KEY, (items) =>
      items.map((event) => {
        if (event.get('team')._id === team._id) return event.set('team', team);
        return event;
      }),
    );

    this.setStateAndTrigger(next);
  },

  onToggleEdit(event) {
    this.setInStateAndTrigger('editingEvent', event);
  },

  onDuplicateCompleted(event) {
    if (this.getInState('activeType') === PAST) {
      AlertActions.show(
        'You have successfully duplicated this event! You can find a copy of it in your active upcoming events.',
        null,
        AlertTypes.successAlert,
      );
    }

    this.appendNew(event);
  },

  onEventUpdatePhotoRealTime(teamId, eventId, event) {
    this.appendNew(event);
  },

  onSaveCompleted(event) {
    /**
     * On save do not overwrite current event state labels.
     * onUpdateLabelsCompleted is responsible for updating event labels.
     */
    delete event.labels;

    this.appendNew(event);
    this._quitEditing();
  },

  onSoftDelete(teamId, eventId) {
    if (!this.getInState([Pagination.PAGE_ITEMS_KEY, eventId])) return null;

    let next = this.getState().deleteIn([Pagination.PAGE_ITEMS_KEY, eventId]);
    this.setStateAndTrigger(next);

    this._subscribeToEvents();
  },

  onRestoreCompleted(event) {
    let eventId = event._id;

    this.appendNew(event);

    if (!this.getInState([Pagination.PAGE_ITEMS_KEY, eventId])) return null;

    let next = this.getState().deleteIn([Pagination.PAGE_ITEMS_KEY, eventId]);
    this.setStateAndTrigger(next);

    RestoreEventActions.populate(event);
  },

  onCancelEdit() {
    this._quitEditing();
  },

  _quitEditing() {
    this.setStateAndTrigger(this.getState().delete('editingEvent'));
  },

  /**
   * Handle incoming real time changes
   */
  onEventMutatedRealTime(teamId, eventId, changes) {
    if (!this.getInState([Pagination.PAGE_ITEMS_KEY, eventId])) return;

    let nextState = this.getState().mergeIn(
      [Pagination.PAGE_ITEMS_KEY, eventId],
      fromJS(changes),
    );

    this.setStateAndTrigger(nextState);
  },

  onCreateCompleted(event) {
    this.appendNew(event);
  },

  onCreateFromProductCompleted(event) {
    this.appendNew(event);
  },

  // eslint-disable-next-line no-unused-vars
  onPopulateFailed(err) {
    //TODO..
  },

  /**
   * Add a new item to the list, or update an existing one.
   */
  appendNew(event) {
    let activeType = this.getInState('activeType');

    /**
     * If active event type is UPCOMING and event is not an upcoming event then return
     */
    if (activeType === UPCOMING && !_upcoming(event)) return null;

    /**
     * If active event type is PAST and event is not a past event then return
     */
    if (activeType === PAST && !_past(event)) return null;

    /**
     * If active event type is DELETED and event is not a deteled event then return
     */
    if (activeType === DELETED && !_deleted(event)) return null;

    let prevState = this.getState(),
      events = prevState.get(Pagination.PAGE_ITEMS_KEY);

    /**
     * If no events, then it hasn't been populated yet, most likely
     */
    if (!events) return;

    let prevEventMap = this.getInState([Pagination.PAGE_ITEMS_KEY, event._id]),
      eventMap = prevEventMap
        ? prevEventMap.merge(fromJS(event))
        : fromJS(event),
      nextEvents = events.set(event._id, eventMap);

    let next = prevState;
    next = next.set(Pagination.PAGE_ITEMS_KEY, nextEvents);
    next = activeType === PAST ? _sortItemsDescending(next) : _sortItems(next);

    this.setStateAndTrigger(next);

    this._subscribeToEvents();
  },

  _subscribeToEvents() {
    if (!this.getInState(Pagination.PAGE_ITEMS_KEY)) return;
    RealTimeActions.subscribeToEvents(
      this.getInState(Pagination.PAGE_ITEMS_KEY),
    );
  },

  /**
   * Helper function for updating all label references in event list
   */
  _updateAllLabelReferences(labelId, changes) {
    let events = OrderedMap(
      this.getInState(Pagination.PAGE_ITEMS_KEY).map((event) => {
        let labels = event.get('labels');

        if (labels) {
          event = event.set(
            'labels',
            labels.map((label) => {
              if (label.get('_id') === labelId) label = label.merge(changes);
              return label;
            }),
          );
        }

        return event;
      }),
    );

    this.setInStateAndTrigger(Pagination.PAGE_ITEMS_KEY, events);
  },

  /**
   * Helper function for removing any label references when a label is deleted my a team
   * that owns the event.
   */
  _removeAllLabelReferences(labelId) {
    let events = OrderedMap(
      this.getInState(Pagination.PAGE_ITEMS_KEY).map((event) => {
        let labels = event.get('labels');
        let labelIndex = null;

        if (labels) {
          //Find the index of a lable on an event
          labelIndex = labels.findIndex((label) => {
            if (label.get('_id') === labelId) return true;
            return false;
          });

          //If label reference exists then remove it.
          if (labelIndex !== -1) labels = labels.splice(labelIndex, 1);

          event = event.set('labels', labels);
        }

        return event;
      }),
    );

    this.setInStateAndTrigger(Pagination.PAGE_ITEMS_KEY, events);
  },

  /**
   * Helper function for updating event labels
   */
  _updateLabelReferencesTransient(eventId, labelIds) {
    let next = this.getState();
    let event = next.getIn([Pagination.PAGE_ITEMS_KEY, eventId]);

    if (!event) return;

    event = event.update('labels', (labels) => {
      return labels.filter((label) => {
        return labelIds.indexOf(label.get('_id')) !== -1;
      });
    });

    next = next.setIn([Pagination.PAGE_ITEMS_KEY, eventId], event);

    this.setStateAndTrigger(next);
  },
};

/**
 * Sort items in ascending order
 */
function _sortItems(state) {
  let next = state || this.getInState();
  return next.update(Pagination.PAGE_ITEMS_KEY, (items) => {
    return sortHelper.sort(items, 'startOn');
  });
}

/**
 * Sort items in descending order
 */
function _sortItemsDescending(state) {
  let next = state || this.getInState();
  return next.update(Pagination.PAGE_ITEMS_KEY, (items) => {
    return sortHelper.sortDescending(items, 'startOn');
  });
}

/**
 * Check if event is upcoming
 */
function _upcoming(event) {
  return moment(event.endOn).isAfter(
    moment().hour(0).minute(0).subtract(12, 'hours'),
  );
}

/**
 * Check if event is past
 */
function _past(event) {
  return moment(event.endOn).isBefore(
    moment().hour(0).minute(0).subtract(12, 'hours'),
  );
}

/**
 * Check if event is deleted
 */
function _deleted(event) {
  return event.deleted;
}
